import type { TronRes } from './constants'
import type { WalletGetAccount, WalletGetAccountResource } from './type'
import axios from '@peng_kai/kit/libs/axios'
import { queryClient } from '~/query-client'
import { tron } from '~/utils/tron-v2/index'
import { differentFormats } from '~/utils/tron-v2/tronError'
import { bitpie } from '../bitpie'
import { TronChainId, tronGridGatewayByChainId, tronScanGatewayByChainId } from './constants'
import { tokens } from './token/tokens'
import { throwErrorType, tronErrors } from './tronError'

export { Tron }
//  todo 凯没有把没有tron插件的情况考虑进去
const server = axios.create({ timeout: 10 * 1000 })
interface IDelegateResourceParams {
  frozenBalance: number
  ownerAddress: string
  receiveAddress: string
  resourceType: number | string
  isMultiSign: boolean
  multiSignIfo: any
  isLock: boolean
  lockPeriod: number
  options?: any
}
class Tron {
  // public address: undefined | string;
  // public chainId: undefined | string;

  public get tronLink(): Record<string, any> | undefined {
    if (import.meta.env.SSR)
      return
    return (window as any)?.tronLink
  }

  public get tronWeb(): Record<string, any> | undefined {
    if (import.meta.env.SSR)
      return
    return (window as any)?.tronWeb
  }

  public get tron(): Record<string, any> | undefined {
    if (import.meta.env.SSR)
      return
    return (window as any)?.tron
  }

  /** 账户地址 */
  public get address() {
    const address: string = this.tronWeb?.defaultAddress?.base58

    return address || ''
  }

  /** 校验地址 */
  public isAddress(address: any) {
    return !!this.tronWeb?.isAddress(address)
  }

  /** 计算 */
  public fromSun(value: any) {
    return this.tronWeb?.fromSun(value) ? Number(this.tronWeb?.fromSun(value)) : 0
  }

  /** 计算 取整 */
  public fromSunToInteger(value: any) {
    const integer = this.tronWeb?.fromSun(value) ? Number(this.tronWeb?.fromSun(value)) : 0
    return Math.floor(integer)
  }

  /** 连接钱包 */
  public async connect() {
    // 优先使用 eth_requestAccounts 方法，因为它支持在钱包锁定状态下拉起登录弹窗
    if (this.tron) {
      try {
        const res = await this.tron.request({ method: 'eth_requestAccounts' })
      }
      catch (err: any) {
        if (err?.code === -32000)
          throw tronErrors.CONNECT_REQUEST_FREQUENTLY(err)
        if (err?.code === -32002)
          throw tronErrors.CONNECT_WALLET_NOT_CLOSED(err)
        if (err?.code === 4001)
          throw tronErrors.CONNECT_WALLET_REJECTED(err)

        throw tronErrors.CONNECT_WALLET_ERROR(err)
      }
      return true
    }

    if (this.tronWeb) {
      const request = this.tronLink?.request ?? this.tronWeb.request
      const res = await request({ method: 'tron_requestAccounts' })
      if (!res)
        throw tronErrors.WALLET_LOCKED(res)
      if (res?.code === 4000)
        throw tronErrors.CONNECT_WALLET_NOT_CLOSED(res)
      if (res.code === 4001)
        throw tronErrors.CONNECT_WALLET_REJECTED(res)

      return true
    }
  }

  /** 检测是否下载有tronlink插件 */
  public supported() {
    return !!(window as any)?.tronWeb
  }

  /** 获取网络 ID */
  public async fetchChainId() {
    let chainId = ''
    /* 通过网络请求获取 ChainID（准确） */
    try {
      const { blockID = '' } = await queryClient.fetchQuery({
        staleTime: 2000,
        queryKey: ['tron-block-by-number'],
        queryFn: () => this.tronWeb?.trx?.getBlockByNumber?.(0) ?? null,
      })
      chainId = `0x${blockID.slice(-8)}`
    }
    catch (err) {
      throw tronErrors.GET_CAHIN_ERROR(err)
    }

    if (!Object.values(TronChainId).includes(chainId as any))
      throw tronErrors.NOT_SUPPORTED_CAHIN()

    return chainId as TronChainId

    /* 通过 tronWeb 本地获取 ChainID（快速） */
    // const host = this.tronWeb?.fullNode?.host
    // if (typeof host === 'string')
    //   chainId = host === 'https://api.trongrid.io' ? TronChainId.Main : TronChainId.Nile
    //
    // return chainId
  }

  /**
   * 获取账号信息
   * @param address 账户地址
   */
  public async fetchAccount(address: string) {
    const chainId = await this.fetchChainId()
    const url = `${tronGridGatewayByChainId.get(chainId)}/wallet/getaccount`
    const res = await queryClient.fetchQuery({
      staleTime: 2000,
      queryKey: [url, address],
      queryFn: () => {
        return server.request({
          url,
          method: 'POST',
          data: {
            address: this.tronWeb?.address.toHex(address),
          },
        })
      },
    })

    if (res.status === 200)
      return res.data as WalletGetAccount
  }

  /**
   * 获取账户的资源信息
   * @param address 账户地址
   */
  public async fetchAccountResource(address: string) {
    const chainId = await this.fetchChainId()
    const url = `${tronGridGatewayByChainId.get(chainId)}/wallet/getaccountresource`
    const res = await queryClient.fetchQuery({
      staleTime: 2000,
      queryKey: [url, address],
      queryFn: () => {
        return server.request({
          url,
          method: 'POST',
          data: {
            address: this.tronWeb?.address.toHex(address),
          },
        })
      },
    })

    if (res.status === 200)
      return res.data as WalletGetAccountResource
  }

  /**
   * 获取最大可委托量
   * @param address 账户地址
   * @param resourceType 资源类型
   */
  public async fetchCanDelegatedMaxSize(address: string, resourceType: TronRes) {
    const chainId = await this.fetchChainId()
    const url = `${tronGridGatewayByChainId.get(chainId)}/wallet/getcandelegatedmaxsize`
    const res = await queryClient.fetchQuery({
      staleTime: 2000,
      queryKey: [url, address, resourceType],
      queryFn: () => {
        return server.request({
          url,
          method: 'POST',
          data: {
            owner_address: this.tronWeb?.address.toHex(address),
            type: resourceType,
          },
        })
      },
    })

    if (res.status === 200)
      return res.data?.max_size as number
  }

  /**
   * 质押V2
   * @param params 质押参数
   * @param params.amount 质押金额
   * @param params.resourceType 资源类型
   * @param params.address 账户地址
   */
  public async freezeBalanceV2(params: {
    amount: number
    resourceType: TronRes
    address: string
    options?: any
  }): Promise<any> {
    try {
      const result = await this.tronWeb?.transactionBuilder.freezeBalanceV2(
        params.amount,
        params.resourceType,
        params.address,
        params.options,
      )
      const signedTx = await this.tronWeb?.trx.sign(result)
      return await this.tronWeb?.trx.sendRawTransaction(signedTx)
    }
    catch (err) {
      throwErrorType(err)
    }
  }

  /**
   * 质押V2（多签）
   * @param params 质押参数
   * @param params.isMulti 是否多签
   * @param params.permissionId 多签权限ID
   * @param params.isOwnerPermission 是否是 owner 权限
   * @param params.amount 质押金额
   * @param params.resourceType 资源类型
   * @param params.address 账户地址
   */
  public async freezeBalanceV2ByMultiSign(params: {
    permissionId: number
    isOwnerPermission: boolean
    amount: number
    resourceType: TronRes
    address: string
    options?: any
  }): Promise<any> {
    try {
      const result = await this.tronWeb?.transactionBuilder.freezeBalanceV2(
        params.amount,
        params.resourceType,
        params.address,
        params.options,
      )

      const multiSign = await this.tronWeb?.trx.multiSign(result, '', params.permissionId)
      if (
        params.isOwnerPermission
        && params.permissionId
        && multiSign.raw_data.contract[0]?.Permission_id === undefined
      ) {
        if (client.isPc)
          throw tronErrors.MULTISIGN_ERROR()
        else
          throw tronErrors.MULTISIGN_ERROR_UNPC()
      }

      if (multiSign.raw_data.contract[0]?.Permission_id === 0 && client.isMobile)
        throw tronErrors.MULTISIGN_ERROR_UNPC()

      const broastTx = await this.tronWeb?.trx.sendRawTransaction(multiSign)

      if ('code' in broastTx)
        throw new Error(broastTx.code)

      return broastTx
    }
    catch (err) {
      throwErrorType(err)
    }
  }

  /**
   * 解压
   */
  public async unfreezeBalance() {}

  /**
   * 委托（代理）
   */
  public async delegateResource() {}

  /**
   * 获取币种余额
   * @param address 账户地址
   * @param chainId 链ID
   * @param symbol 代币 Symbol，默认为 TRX
   */
  public async fetchBalance(address: string, chainId: string, symbol = 'TRX') {
    const _symbol = symbol.toUpperCase()
    const token = tokens.find(t => t.symbol === _symbol)
    if (token)
      return await token.fetchBalance(address, chainId)
    else return 0
  }

  /**
   * 转账
   * @param params 转账参数
   * @param params.fromAddress 转出地址
   * @param params.toAddress 转入地址
   * @param params.chainId 链ID
   * @param params.symbol 代币 Symbol
   * @param params.amount 转账金额
   * @param params.broadcast 是否广播
   */
  public async transfer(params: {
    fromAddress: string
    toAddress: string
    chainId?: string
    symbol: string
    amount: string
    bandwidthSurplus?: number
    trxSurplus?: number
    energyTrxSurplus?: number
    broadcast?: boolean
  }) {
    const _symbol = params.symbol.toUpperCase()
    const token = tokens.find(t => t.symbol === _symbol)
    if (token) {
      return await token.transfer({
        ...params,
        broadcast: params.broadcast ?? false,
      })
    }
  }

  /**
   * 代理的订单列表
   * @param limit
   * @param start
   * @param type
   */
  public async fetchAgencyOrders(params: { address: string, limit: number, start: number, type: number }) {
    const chainId = await this.fetchChainId()
    const url = `${tronScanGatewayByChainId.get(chainId)}/account/resourcev2`
    const res = await queryClient.fetchQuery({
      queryKey: [url, params],
      queryFn: () => {
        return server.request({
          url,
          method: 'GET',
          params: {
            address: params.address,
            limit: params.limit,
            start: params.start,
            type: params.type,
          },
        })
      },
    })

    if (res.status === 200)
      return res as any
  }

  /**
   * 撤销资源代理
   * @param params
   */
  public async undelegateResource(params: {
    amount: number | undefined
    receiverAddress: string
    resource: 'BANDWIDTH' | 'ENERGY'
    address: string
    options?: any
  }) {
    const transaction = await this.tronWeb?.transactionBuilder.undelegateResource(
      params.amount,
      params.receiverAddress,
      params.resource,
      params.address,
    )
    const signedTx = await this.tronWeb?.trx.sign({ ...transaction, __options: params.options })
    const balance = await this.tronWeb?.trx.getBalance(params.receiverAddress)
    console.log('balance', balance)
    console.log('balance', String(balance))

    const broastTx = await this.tronWeb?.trx.sendRawTransaction(signedTx)
    console.log('signedTx', signedTx)
    console.log('broastTx', broastTx)
    return broastTx
  }

  /**
   * 拉起钱包
   * @param params
   */
  public async sendTrx(params: {
    toAddress: string | undefined
    cancelAmount: number
    payAddress: string | undefined
  }) {
    const amount = this.tronWeb?.toSun(params.cancelAmount)

    const signedTx = await this.tronWeb?.transactionBuilder.sendTrx(params.toAddress, amount, params.payAddress)
    return signedTx
  }

  /**
   * 获取投票数额
   * @address string
   */
  public async getAccountResources(address: string) {
    const res = await this.tronWeb?.trx?.getAccountResources(address)
    return res
  }

  /**
   * 投票
   * @param address
   * @param votes
   * @param options
   */
  public async vote(addeess: string, votes: any, options?: any) {
    const votesResp = await this.tronWeb?.transactionBuilder.vote(votes, addeess)
    const signedTRX = await this.tronWeb?.trx.sign(votesResp)
    const broastTRX = await this.tronWeb?.trx.sendRawTransaction(signedTRX)
    return broastTRX
  }

  /**
   * 获取权限
   * @param hex 权限 hex
   * @param id 匹配到的权限ID
   */
  public getPermission(hex: string, id: number) {
    let op = ''
    const hexSplit = hex.split('')
    hexSplit.forEach((h, index) => {
      const f2 = Number.parseInt(h, 16).toString(2)
      const f24 = f2.padStart(4, '0')
      const f24reverse = f24.split('').reverse().join('')
      if (index % 2 === 1 && index !== hexSplit.length - 1)
        op += `${f24reverse}:`
      else
        op += f24reverse
    })
    let permission = ''
    op.split(':').forEach((e) => {
      permission += e.substring(4, 8) + e.substring(0, 4)
    })
    const ids: number[] = []
    permission.split('').forEach((e, index) => {
      if (Number(e) === 1)
        ids.push(index)
    })
    return ids.includes(id)
  }

  /**
   * 委托出售
   * @param params
   */
  public async getDelegateResourceSign(params: {
    c2CPreDelegateResp: any
    isMultiSign: boolean
    permissionId: number
    isOwnerPermission: boolean
  }) {
    try {
      if (toValue(params.isMultiSign)) {
        const res = await this.tronWeb?.trx.multiSign(params.c2CPreDelegateResp, '', params.permissionId)
        if (
          !params.isOwnerPermission
          && params.permissionId > 0
          && res.raw_data.contract[0]?.Permission_id === undefined
        ) {
          if (client.isPc)
            throw tronErrors.MULTISIGN_ERROR()
          else
            throw tronErrors.MULTISIGN_ERROR_UNPC()
        }

        if ('raw_data' in res)
          return res
        throw tronErrors.SIGN_ERROR()
      }
      else {
        const res = await this.tronWeb?.trx.sign(params.c2CPreDelegateResp)
        if ('raw_data' in res)
          return res
        throw tronErrors.SIGN_ERROR()
      }
    }
    catch (e) {
      throwErrorType(e)
    }
  }

  /**
   * 委托 后端广播
   * @param params
   */
  public async delegateResourceServerBroadcast(params: IDelegateResourceParams) {
    try {
      const delegateResourceTx = await this.tronWeb?.transactionBuilder.delegateResource(
        params.frozenBalance,
        params.receiveAddress,
        params.resourceType,
        params.ownerAddress,
        params.isLock,
        params.lockPeriod,
        params?.options,
      )

      if (toValue(params.isMultiSign)) {
        const res = await this.tronWeb?.trx.multiSign(delegateResourceTx, '', params.multiSignIfo.permissionId)
        if (
          !params.multiSignIfo.isOwnerPermission
          && params.multiSignIfo.permissionId > 0
          && res.raw_data.contract[0]?.Permission_id === undefined
        ) {
          if (client.isPc)
            throw tronErrors.MULTISIGN_ERROR()
          else
            throw tronErrors.MULTISIGN_ERROR_UNPC()
        }
        return res
      }
      else {
        const res = await this.tronWeb?.trx.sign(delegateResourceTx)
        if ('raw_data' in res)
          return res
        throw tronErrors.SIGN_ERROR()
      }
    }
    catch (e) {
      throwErrorType(e)
    }
  }

  public async payTRXServerBroadcast(params: any) {
    try {
      const amount = this.tronWeb?.toSun(params.amount)
      const tx = await this.tronWeb?.transactionBuilder.sendTrx(params.toAddress, amount, params.fromAddress)
      return await this.tronWeb?.trx.sign(tx)
    }
    catch (err) {
      throwErrorType(err)
    }
  }

  public async fetchCanwithdrawunfreezeamount(address: string) {
    // TODO: 暂时返回 {}
    return {}

    // const chainId = await this.fetchChainId()
    // const url = `${tronGridGatewayByChainId.get(chainId)}/wallet/getcanwithdrawunfreezeamount`
    // const res = await queryClient.fetchQuery({
    //   staleTime: 2000,
    //   queryKey: [url, address],
    //   queryFn: () => {
    //     return server.request({
    //       url,
    //       method: 'POST',
    //       data: {
    //         owner_address: this.tronWeb?.address.toHex(address),
    //       },
    //     })
    //   },
    // })

    // if (res.status === 200)
    //   return res.data
  }

  public async fetchWithdrawExpireUnfreeze(address: string) {
    const chainId = await this.fetchChainId()
    const url = `${tronGridGatewayByChainId.get(chainId)}/wallet/withdrawexpireunfreeze`
    const res = await queryClient.fetchQuery({
      staleTime: 2000,
      queryKey: [url, address],
      queryFn: () => {
        return server.request({
          url,
          method: 'POST',
          data: {
            owner_address: this.tronWeb?.address.toHex(address),
          },
        })
      },
    })

    if (res.status === 200)
      return res.data
  }

  public async switchChain() {
    try {
      return await this?.tron?.request({
        method: 'wallet_switchEthereumChain',
        params: [{ chainId: TronChainId.Main }],
      })
    }
    catch (error) {
      aMessage.error($t('ngs_0u_W0J5i3iMqdlmOi'))
    }
  }

  /**
   * 空签名
   */
  public async emptySign(signContent: string) {
    const resp = { res: null, err: 'Not signedTx' }
    if (!this.tronWeb) {
      resp.err = 'tronLink error:tronLink not Connect'
      return resp
    }
    try {
      const isBitpie = bitpie.isBitpie()
      const toHexFn = isBitpie ? bitpie.toHex : this.tronWeb.toHex
      if (!signContent || !toHexFn)
        return resp

      const signedTx = await this.tronWeb?.trx?.sign?.(toHexFn?.(signContent))
      if (!signedTx)
        return resp
      if (typeof signedTx !== 'string' && isBitpie) {
        resp.err = 'Confirmation declined by user'
        return resp
      }
      resp.res = signedTx
      resp.err = ''
      return resp
    }
    catch (error: any) {
      if (
        differentFormats(error, 'Confirmation declined by user')
        || differentFormats(error, 'cancel')
        || differentFormats(error, 'cancle')
        || differentFormats(error, '拒绝')
        || differentFormats(error, '取消')
      ) {
        resp.err = 'Confirmation declined by user'
      }
      else if (error && typeof error === 'string') {
        resp.err = error
      }
      return resp
    }
  }

  /*
  * U换T拉起签名，签名内容获取后端内容
  * */
  public async UTTSign(signed: any) {
    if (signed) {
      try {
        return await tron.tronWeb?.trx.sign(signed)
      }
      catch (e) {
        throwErrorType(e)
      }
    }
  }
}
